import { useMemo, useState } from 'react';
import {
  useBillingHardwareCatalog,
  type HardwareCatalogProduct,
  type HardwareRegion,
} from './useBillingConsole';

/**
 * One line of a device order. `optionKey` is `sku` or `sku|price_…` — it identifies WHICH
 * mapping row to bill, since one catalog product can carry several Stripe-priced
 * configurations (OptiKiosk 32 wall mount vs with stand).
 */
export type HardwareLineRow = { optionKey: string; quantity: string };

/** `sku|price_…`, or just `sku` when the product is priced from the catalog. */
export const optionKeyOf = (sku: string, stripePriceId: string) =>
  stripePriceId ? sku + '|' + stripePriceId : sku;

const EMPTY_ROW: HardwareLineRow = { optionKey: '', quantity: '1' };

/** Lines dropped by a currency or region change, with WHY. */
export interface PrunedLines {
  names: string[];
  reason: string;
}

/**
 * Everything two cards need to let an operator pick devices and quantities: Create Hardware
 * Invoice (a standalone hardware invoice) and PO / Invoice - Onboarding Process (devices billed
 * on a new subscription's first invoice).
 *
 * Extracted rather than copied because the behaviour here is about MONEY and must not diverge
 * between the two callers:
 *
 *   - which products are even offerable (priced in the chosen currency, mapped to a Stripe
 *     product, and shippable to the chosen region — kiosks are US-only);
 *   - dropping lines that a currency or region change invalidated, and NAMING what was dropped
 *     with the reason, because "not priced in EUR" and "does not ship internationally" send an
 *     operator to different fixes;
 *   - refusing two lines for the SAME configuration (wall mount + with stand is fine).
 *
 * What it deliberately does NOT do is preview shipping or tax. Both live server-side, and
 * duplicating that arithmetic in the client is exactly how a preview starts disagreeing with the
 * invoice. `subtotal` is hardware only.
 */
export function useDeviceLines(args: { currency: string; region: HardwareRegion }) {
  const { currency, region } = args;
  const [rows, setRows] = useState<HardwareLineRow[]>([{ ...EMPTY_ROW }]);
  const [pruned, setPruned] = useState<PrunedLines | null>(null);

  const { data, loading: catalogLoading, error: catalogError } = useBillingHardwareCatalog();
  const catalog = data?.staffBillingHardwareCatalog;
  const allProducts: HardwareCatalogProduct[] = catalog?.products ?? [];

  /**
   * Priced in the selected currency, mapped to a Stripe product, AND shippable to the selected
   * region — offering a kiosk under International would only produce a server refusal at submit.
   */
  const options = useMemo(
    () =>
      allProducts.filter(
        (p) =>
          p.invoiceable &&
          p.prices.some((x) => x.currency === currency) &&
          !(region === 'international' && p.usOnly),
      ),
    [allProducts, currency, region],
  );

  const byKey = useMemo(
    () => new Map(options.map((p) => [optionKeyOf(p.sku, p.stripePriceId), p])),
    [options],
  );

  const unitAmount = (key: string): number =>
    byKey.get(key)?.prices.find((x) => x.currency === currency)?.amount ?? 0;

  const productName = (key: string): string =>
    byKey.get(key)?.name ??
    allProducts.find((p) => optionKeyOf(p.sku, p.stripePriceId) === key)?.name ??
    key;

  /** Hardware only — see the hook docblock on why shipping and tax are not previewed. */
  const subtotal = rows.reduce(
    (sum, r) => sum + unitAmount(r.optionKey) * (parseInt(r.quantity, 10) || 0),
    0,
  );

  const filledRows = rows.filter((r) => r.optionKey);
  const duplicateSku = filledRows
    .map((r) => r.optionKey)
    .find((key, i, arr) => arr.indexOf(key) !== i);
  const badQuantity = filledRows.some((r) => !/^\d+$/.test(r.quantity) || Number(r.quantity) < 1);

  /** True when the lines themselves are billable. Callers add their own gates on top. */
  const linesValid = filledRows.length > 0 && !duplicateSku && !badQuantity;

  /**
   * Changing currency can invalidate lines — kiosks are USD-only. Drop them and name what was
   * dropped rather than silently repricing to something else. Returns nothing; the caller still
   * owns the currency state, because for op 2 the currency is the SUBSCRIPTION's.
   */
  const pruneForCurrency = (next: string) => {
    const priced = new Set(
      allProducts
        .filter((p) => p.invoiceable && p.prices.some((x) => x.currency === next))
        .map((p) => optionKeyOf(p.sku, p.stripePriceId)),
    );
    const dropped = rows.filter((r) => r.optionKey && !priced.has(r.optionKey));
    setPruned({
      names: dropped.map((r) => productName(r.optionKey)),
      reason: `not priced in ${next.toUpperCase()} in the device catalog`,
    });
    const kept = rows.filter((r) => !r.optionKey || priced.has(r.optionKey));
    setRows(kept.length ? kept : [{ ...EMPTY_ROW }]);
  };

  /** Switching to International invalidates any kiosk line — we don't ship them abroad yet. */
  const pruneForRegion = (next: HardwareRegion) => {
    if (next !== 'international') {
      setPruned(null);
      return;
    }
    const usOnlyKeys = new Set(
      allProducts.filter((p) => p.usOnly).map((p) => optionKeyOf(p.sku, p.stripePriceId)),
    );
    const dropped = rows.filter((r) => r.optionKey && usOnlyKeys.has(r.optionKey));
    setPruned({
      names: dropped.map((r) => productName(r.optionKey)),
      reason: 'not shipped internationally yet — kiosks are US-only',
    });
    const kept = rows.filter((r) => !r.optionKey || !usOnlyKeys.has(r.optionKey));
    setRows(kept.length ? kept : [{ ...EMPTY_ROW }]);
  };

  /** `{ sku, stripePriceId, quantity }[]` — catalog references and counts, never money. */
  const payloadLines = () =>
    filledRows.map((row) => {
      const [sku, stripePriceId = ''] = row.optionKey.split('|');
      return { sku, stripePriceId, quantity: parseInt(row.quantity, 10) };
    });

  const reset = () => {
    setRows([{ ...EMPTY_ROW }]);
    setPruned(null);
  };

  return {
    rows,
    setRows,
    options,
    allProducts,
    catalog,
    catalogLoading,
    catalogError,
    subtotal,
    filledRows,
    duplicateSku,
    badQuantity,
    linesValid,
    pruned,
    setPruned,
    pruneForCurrency,
    pruneForRegion,
    productName,
    payloadLines,
    reset,
  };
}
